import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiSearch,
  FiShoppingBag,
  FiCoffee,
  FiSmartphone,
  FiBriefcase,
  FiHeart,
  FiTruck,
  FiChevronRight,
  FiChevronLeft,
  FiStar,
  FiPackage,
  FiGrid,
  FiList,
  FiFilter,
  FiX,
  FiShoppingCart,
  FiPercent,
  FiZap,
  FiAward,
  FiTrendingUp,
  FiShield,
} from 'react-icons/fi';
import { IoStorefrontOutline, IoFastFoodOutline } from 'react-icons/io5';
import { ecommerceService } from '../services';
import type { Store, StoreTipo } from '../types';
import { Card, LoaderComponent, EmptyState } from '../components/ui';
import { useCart } from '../contexts/CartContext';
import { resolveImage } from '../utils/image';

// Icon mapping by icon name (from admin ICONE field) and by description keywords
const ICON_MAP: Record<string, React.ReactNode> = {
  // Common icon names (admin types these in the ICONE field)
  store: <IoStorefrontOutline className="text-xl" />,
  storefront: <IoStorefrontOutline className="text-xl" />,
  shopping_cart: <FiShoppingBag className="text-xl" />,
  shopping_bag: <FiShoppingBag className="text-xl" />,
  restaurant: <FiCoffee className="text-xl" />,
  food: <IoFastFoodOutline className="text-xl" />,
  fast_food: <IoFastFoodOutline className="text-xl" />,
  coffee: <FiCoffee className="text-xl" />,
  phone: <FiSmartphone className="text-xl" />,
  smartphone: <FiSmartphone className="text-xl" />,
  devices: <FiSmartphone className="text-xl" />,
  laptop: <FiSmartphone className="text-xl" />,
  work: <FiBriefcase className="text-xl" />,
  business: <FiBriefcase className="text-xl" />,
  briefcase: <FiBriefcase className="text-xl" />,
  favorite: <FiHeart className="text-xl" />,
  health: <FiHeart className="text-xl" />,
  heart: <FiHeart className="text-xl" />,
  local_shipping: <FiTruck className="text-xl" />,
  truck: <FiTruck className="text-xl" />,
  delivery: <FiTruck className="text-xl" />,
  checkroom: <FiShoppingBag className="text-xl" />,
  shirt: <FiShoppingBag className="text-xl" />,
  star: <FiStar className="text-xl" />,
  // feather icon names (fi- prefix optional)
  'fi-shopping-bag': <FiShoppingBag className="text-xl" />,
  'fi-coffee': <FiCoffee className="text-xl" />,
  'fi-smartphone': <FiSmartphone className="text-xl" />,
  'fi-briefcase': <FiBriefcase className="text-xl" />,
  'fi-heart': <FiHeart className="text-xl" />,
  'fi-truck': <FiTruck className="text-xl" />,
  'fi-star': <FiStar className="text-xl" />,
};

// Fallback: match by description keywords
const DESC_ICON_MAP: Record<string, React.ReactNode> = {
  alimentacao: <IoFastFoodOutline className="text-xl" />,
  restaurante: <FiCoffee className="text-xl" />,
  moda: <FiShoppingBag className="text-xl" />,
  roupa: <FiShoppingBag className="text-xl" />,
  tecnologia: <FiSmartphone className="text-xl" />,
  electronica: <FiSmartphone className="text-xl" />,
  servicos: <FiBriefcase className="text-xl" />,
  saude: <FiHeart className="text-xl" />,
  farmacia: <FiHeart className="text-xl" />,
  entregas: <FiTruck className="text-xl" />,
  supermercado: <IoFastFoodOutline className="text-xl" />,
  mercearia: <IoFastFoodOutline className="text-xl" />,
  bebidas: <FiCoffee className="text-xl" />,
  beleza: <FiStar className="text-xl" />,
  cosmetica: <FiStar className="text-xl" />,
};

const COLOR_MAP: Record<string, string> = {
  alimentacao: 'from-orange-400 to-orange-600',
  restaurante: 'from-amber-400 to-amber-600',
  moda: 'from-pink-400 to-pink-600',
  roupa: 'from-pink-400 to-pink-600',
  tecnologia: 'from-blue-400 to-blue-600',
  electronica: 'from-blue-400 to-blue-600',
  servicos: 'from-indigo-400 to-indigo-600',
  saude: 'from-red-400 to-red-600',
  farmacia: 'from-red-400 to-red-600',
  entregas: 'from-green-400 to-green-600',
  supermercado: 'from-emerald-400 to-emerald-600',
  mercearia: 'from-emerald-400 to-emerald-600',
  bebidas: 'from-amber-400 to-amber-600',
  beleza: 'from-fuchsia-400 to-fuchsia-600',
  cosmetica: 'from-fuchsia-400 to-fuchsia-600',
};

// Safe string coercion (DB may return Buffer, null, or number)
const safeStr = (val: unknown): string => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  return '';
};

// Resolve icon: first try ICONE field from DB, then match by description
const getTypeIcon = (typeDesc?: unknown, iconeName?: unknown): React.ReactNode => {
  // 1. Try exact icon name from DB
  const icone = safeStr(iconeName);
  if (icone) {
    const key = icone.toLowerCase().trim();
    if (ICON_MAP[key]) return ICON_MAP[key];
  }
  // 2. Fallback: match by description keywords
  const desc = safeStr(typeDesc);
  if (desc) {
    const normalized = desc.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    for (const [k, icon] of Object.entries(DESC_ICON_MAP)) {
      if (normalized.includes(k)) return icon;
    }
  }
  return <IoStorefrontOutline className="text-xl" />;
};

const getTypeColor = (typeDesc?: unknown): string => {
  const desc = safeStr(typeDesc);
  if (!desc) return 'from-gray-400 to-gray-600';
  const key = desc.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  for (const [k, color] of Object.entries(COLOR_MAP)) {
    if (key.includes(k)) return color;
  }
  return 'from-gray-400 to-gray-600';
};

// Hero Banner Carousel
const HeroBanner: React.FC<{ stores: Store[]; onStoreClick: (id: string) => void }> = ({
  stores,
  onStoreClick,
}) => {
  const [current, setCurrent] = useState(0);
  const featured = stores.filter((s) => s.DESTAQUE).slice(0, 5);

  useEffect(() => {
    if (featured.length <= 1) return;
    const timer = setInterval(() => setCurrent((p) => (p + 1) % featured.length), 5000);
    return () => clearInterval(timer);
  }, [featured.length]);

  // Fallback generic banner when no featured stores
  if (featured.length === 0) {
    return (
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-pagali-purple via-indigo-600 to-pagali-primary min-h-[200px] md:min-h-[240px]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 rounded-full" />
          <div className="absolute -bottom-32 -left-16 w-64 h-64 bg-white/5 rounded-full" />
          <div className="absolute top-1/3 right-1/3 w-40 h-40 bg-white/5 rounded-full" />
        </div>
        <div className="relative flex flex-col md:flex-row items-center p-6 md:p-10 gap-6">
          <div className="flex-1 text-center md:text-left">
            <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white/90 text-xs px-3 py-1 rounded-full mb-3">
              <FiZap className="text-yellow-300" />
              Bem-vindo ao Pagali Shop
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight">
              O melhor do comércio<br />cabo-verdiano num só lugar
            </h2>
            <p className="text-white/70 mt-2 text-sm md:text-base max-w-lg">
              Descobre lojas locais, compra online e paga com a tua wallet Pagali de forma rápida e segura.
            </p>
            <div className="mt-5 flex items-center gap-4 justify-center md:justify-start">
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <FiTruck /> Entrega rápida
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <FiShield /> Pagamento seguro
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <FiPercent /> Promoções exclusivas
              </div>
            </div>
          </div>
          <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-white/20">
            <FiShoppingBag className="text-5xl text-white/40" />
          </div>
        </div>
      </div>
    );
  }

  const store = featured[current];
  const logoSrc = resolveImage(store.LOGO, store.MIME_TYPE);

  return (
    <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-pagali-purple via-indigo-600 to-pagali-primary min-h-[220px] md:min-h-[260px]">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 rounded-full" />
        <div className="absolute -bottom-32 -left-16 w-64 h-64 bg-white/5 rounded-full" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-white/5 rounded-full" />
      </div>

      <div className="relative flex flex-col md:flex-row items-center p-6 md:p-10 gap-6">
        {/* Text */}
        <div className="flex-1 text-center md:text-left">
          <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white/90 text-xs px-3 py-1 rounded-full mb-3">
            <FiStar className="text-yellow-300" />
            Loja em Destaque
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight">
            {store.NOME}
          </h2>
          {store.DESCRICAO && (
            <p className="text-white/70 mt-2 text-sm md:text-base line-clamp-2 max-w-lg">
              {store.DESCRICAO}
            </p>
          )}
          <div className="mt-5 flex items-center gap-3 justify-center md:justify-start">
            <button
              onClick={() => onStoreClick(store.ID)}
              className="bg-white text-pagali-purple px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-white/90 transition-colors shadow-lg"
            >
              Visitar Loja
            </button>
            {store.PRODUCT_COUNT !== undefined && (
              <span className="text-white/60 text-sm">
                {store.PRODUCT_COUNT} produtos
              </span>
            )}
          </div>
        </div>

        {/* Logo */}
        <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center overflow-hidden flex-shrink-0 border border-white/20">
          {logoSrc ? (
            <img src={logoSrc} alt={store.NOME} className="w-full h-full object-contain p-2" />
          ) : (
            <FiShoppingBag className="text-5xl text-white/50" />
          )}
        </div>
      </div>

      {/* Navigation */}
      {featured.length > 1 && (
        <>
          <button
            onClick={() => setCurrent((p) => (p - 1 + featured.length) % featured.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/30 transition-colors"
          >
            <FiChevronLeft />
          </button>
          <button
            onClick={() => setCurrent((p) => (p + 1) % featured.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/30 transition-colors"
          >
            <FiChevronRight />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {featured.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrent(idx)}
                className={`h-1.5 rounded-full transition-all ${
                  idx === current ? 'bg-white w-8' : 'bg-white/30 w-1.5'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Promo Strip
const PromoStrip: React.FC = () => (
  <div className="flex gap-4 overflow-x-auto pb-1 scrollbar-hide">
    {[
      { icon: <FiTruck />, text: 'Entrega Grátis acima de 5.000 ECV', color: 'text-green-600 bg-green-50' },
      { icon: <FiPercent />, text: 'Até 30% desconto em lojas selecionadas', color: 'text-orange-600 bg-orange-50' },
      { icon: <FiZap />, text: 'Pagamento instantâneo com Pagali', color: 'text-blue-600 bg-blue-50' },
    ].map((promo, i) => (
      <div
        key={i}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap text-sm font-medium ${promo.color}`}
      >
        {promo.icon}
        {promo.text}
      </div>
    ))}
  </div>
);

// Category Card for grid display
const CategoryCard: React.FC<{
  type: StoreTipo;
  isSelected: boolean;
  onClick: () => void;
}> = ({ type, isSelected, onClick }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all min-w-[80px] cursor-pointer ${
      isSelected
        ? 'bg-pagali-purple text-white shadow-lg shadow-pagali-purple/30'
        : 'bg-white border border-gray-100 text-gray-600 hover:border-pagali-purple/40 hover:shadow-md'
    }`}
  >
    <div
      className={`w-11 h-11 rounded-xl flex items-center justify-center ${
        isSelected ? 'bg-white/20' : `bg-gradient-to-br ${getTypeColor(type.DESCRICAO)} text-white`
      }`}
    >
      {getTypeIcon(type.DESCRICAO, type.ICONE)}
    </div>
    <span className="text-xs font-medium whitespace-nowrap">{type.DESCRICAO}</span>
  </button>
);

// Store Card - redesigned
const StoreCard: React.FC<{ store: Store; onClick: () => void }> = ({ store, onClick }) => {
  const logoSrc = resolveImage(store.LOGO, store.MIME_TYPE);
  const bannerGradient = getTypeColor(store.CATEGORIA_NOME || store.DESCRICAO);
  const initial = (store.NOME || '?').charAt(0).toUpperCase();

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl border border-gray-100 hover:border-pagali-purple/30 hover:shadow-lg transition-all cursor-pointer group"
    >
      {/* Banner */}
      <div className={`relative h-28 overflow-hidden rounded-t-2xl bg-gradient-to-br ${bannerGradient}`}>
        {store.BANNER_IMAGE ? (
          <img src={store.BANNER_IMAGE} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-7xl font-black text-white/10 select-none leading-none">{initial}</span>
          </div>
        )}
        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex gap-1.5">
          {store.NOVO && <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">NOVO</span>}
          {store.DESTAQUE && (
            <span className="bg-white/90 text-orange-500 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-0.5">
              <FiStar className="text-[8px]" /> Destaque
            </span>
          )}
        </div>
      </div>

      {/* Content — logo puxado para cima com -mt-7 para sobrepor o banner */}
      <div className="px-3.5 pb-3">
        <div className="-mt-7 mb-2 relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-white shadow-lg border-[3px] border-white overflow-hidden flex items-center justify-center">
            {logoSrc ? (
              <img src={logoSrc} alt={store.NOME} className="w-full h-full object-contain p-1" />
            ) : (
              <div className={`w-full h-full bg-gradient-to-br ${bannerGradient} flex items-center justify-center`}>
                <span className="text-xl font-black text-white">{initial}</span>
              </div>
            )}
          </div>
        </div>
        <h3 className="font-semibold text-gray-900 group-hover:text-pagali-purple transition-colors truncate text-sm">
          {store.NOME}
        </h3>
        {store.CATEGORIA_NOME && (
          <span className="inline-block mt-1 text-[11px] bg-pagali-purple/8 text-pagali-purple px-2 py-0.5 rounded-full font-medium">
            {store.CATEGORIA_NOME}
          </span>
        )}
        {store.DESCRICAO && (
          <p className="text-xs text-gray-400 mt-1.5 line-clamp-2 leading-relaxed">{store.DESCRICAO}</p>
        )}
        <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-gray-50">
          <div className="flex items-center gap-2.5 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <FiStar className="text-yellow-400" />
              <span className="text-gray-600 font-medium">{store.RATING ? store.RATING.toFixed(1) : 'Novo'}</span>
            </span>
            {store.PRODUCT_COUNT !== undefined && (
              <span className="flex items-center gap-1"><FiPackage />{store.PRODUCT_COUNT}</span>
            )}
          </div>
          <span className="text-xs text-pagali-purple font-medium flex items-center gap-0.5">
            Ver <FiChevronRight className="text-[10px]" />
          </span>
        </div>
      </div>
    </div>
  );
};

// Horizontal scroll store row
const StoreRow: React.FC<{
  title: string;
  icon: React.ReactNode;
  stores: Store[];
  onStoreClick: (id: string) => void;
  onViewAll?: () => void;
}> = ({ title, icon, stores, onStoreClick, onViewAll }) => {
  if (stores.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-lg font-semibold text-pagali-gray-800">{title}</h2>
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-sm text-pagali-purple font-medium hover:underline flex items-center gap-1"
          >
            Ver todas <FiChevronRight className="text-xs" />
          </button>
        )}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
        {stores.map((store) => (
          <div key={store.ID} className="min-w-[280px] max-w-[280px] flex-shrink-0">
            <StoreCard store={store} onClick={() => onStoreClick(store.ID)} />
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Component
const Ecommerce: React.FC = () => {
  const navigate = useNavigate();
  const { paramId } = useParams<{ paramId?: string }>();
  const { itemCount, cartTotal } = useCart();

  const [storeTypes, setStoreTypes] = useState<StoreTipo[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedType, setSelectedType] = useState<string | null>(paramId || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Pagali Shop');
    loadStoreTypes();
  }, []);

  useEffect(() => {
    loadStores();
  }, [selectedType]);

  const loadStoreTypes = async () => {
    try {
      const data = await ecommerceService.getStoreTypes();
      setStoreTypes(data);
    } catch (err) {
      console.error('Error loading store types:', err);
    }
  };

  const loadStores = async () => {
    setLoading(true);
    try {
      const data = selectedType
        ? await ecommerceService.getStoresByType(selectedType)
        : await ecommerceService.getStores();
      const sorted = data.sort((a, b) => {
        if (a.DESTAQUE && !b.DESTAQUE) return -1;
        if (!a.DESTAQUE && b.DESTAQUE) return 1;
        return a.NOME.localeCompare(b.NOME);
      });
      setStores(sorted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar lojas');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreClick = (storeId: string) => {
    navigate(`/ecommerce/store/${storeId}`);
  };

  const filteredStores = useMemo(() => {
    if (!searchQuery.trim()) return stores;
    const query = searchQuery.toLowerCase();
    return stores.filter(
      (store) =>
        store.NOME.toLowerCase().includes(query) ||
        store.DESCRICAO?.toLowerCase().includes(query) ||
        store.CATEGORIA_NOME?.toLowerCase().includes(query)
    );
  }, [stores, searchQuery]);

  const featuredStores = useMemo(() => stores.filter((s) => s.DESTAQUE), [stores]);
  const newStores = useMemo(() => stores.filter((s) => s.NOVO), [stores]);
  const isSearching = searchQuery.trim().length > 0;
  const showSections = !isSearching && !selectedType;

  return (
    <div className="space-y-6 pb-24 px-4 md:px-6 lg:px-8 max-w-screen-2xl mx-auto">
      {/* Top Bar */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-pagali-gray-800">Pagali Shop</h1>
          <p className="text-pagali-gray-500 text-sm">O melhor do comércio cabo-verdiano</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/ecommerce/orders')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-pagali-gray-50 text-pagali-gray-700 hover:bg-pagali-gray-100 transition-colors text-sm font-medium"
          >
            <FiPackage className="text-base" />
            Encomendas
          </button>
          {itemCount > 0 && (
            <button
              onClick={() => navigate('/ecommerce/checkout')}
              className="relative flex items-center gap-2 px-4 py-2 rounded-xl bg-pagali-purple text-white hover:bg-pagali-purple-light transition-colors text-sm font-medium"
            >
              <FiShoppingCart />
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {itemCount}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className={`relative transition-all ${searchFocused ? 'z-20' : ''}`}>
        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-pagali-gray-400 text-lg" />
        <input
          type="text"
          placeholder="Pesquisar lojas, produtos, categorias..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          className={`w-full pl-12 pr-12 py-3.5 bg-white rounded-2xl border outline-none transition-all text-sm ${
            searchFocused
              ? 'border-pagali-purple ring-4 ring-pagali-purple/10 shadow-lg'
              : 'border-pagali-gray-200 shadow-sm'
          }`}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-pagali-gray-400 hover:text-pagali-gray-600"
          >
            <FiX />
          </button>
        )}
      </div>

      {/* Hero Banner - always show when not searching */}
      {!loading && showSections && (
        <HeroBanner stores={stores} onStoreClick={handleStoreClick} />
      )}

      {/* Promo Strip */}
      {showSections && <PromoStrip />}

      {/* Categories */}
      {storeTypes.length > 0 && (
        <div>
          {!isSearching && (
            <h2 className="text-lg font-semibold text-pagali-gray-800 mb-3">Categorias</h2>
          )}
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setSelectedType(null)}
              className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all min-w-[80px] cursor-pointer ${
                !selectedType
                  ? 'bg-pagali-purple text-white shadow-lg shadow-pagali-purple/30'
                  : 'bg-white border border-gray-100 text-gray-600 hover:border-pagali-purple/40 hover:shadow-md'
              }`}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                !selectedType ? 'bg-white/20' : 'bg-gradient-to-br from-gray-400 to-gray-600 text-white'
              }`}>
                <FiGrid className="text-xl" />
              </div>
              <span className="text-xs font-medium">Todas</span>
            </button>
            {storeTypes.map((type) => (
              <CategoryCard
                key={type.ID}
                type={type}
                isSelected={selectedType === type.ID}
                onClick={() => setSelectedType(selectedType === type.ID ? null : type.ID)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="py-20">
          <LoaderComponent size="md" text="A carregar lojas..." />
        </div>
      ) : error ? (
        <Card className="text-center py-12">
          <p className="text-pagali-error mb-4">{error}</p>
          <button
            onClick={() => loadStores()}
            className="text-pagali-purple font-medium hover:underline"
          >
            Tentar novamente
          </button>
        </Card>
      ) : isSearching || selectedType ? (
        /* Search/Filter Results */
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-pagali-gray-500">
              {filteredStores.length} loja{filteredStores.length !== 1 ? 's' : ''} encontrada
              {filteredStores.length !== 1 ? 's' : ''}
              {searchQuery && (
                <span>
                  {' '}para <strong>"{searchQuery}"</strong>
                </span>
              )}
            </p>
            <div className="flex items-center gap-1 bg-pagali-gray-50 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-pagali-purple shadow-sm'
                    : 'text-pagali-gray-400'
                }`}
              >
                <FiGrid className="text-sm" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-pagali-purple shadow-sm'
                    : 'text-pagali-gray-400'
                }`}
              >
                <FiList className="text-sm" />
              </button>
            </div>
          </div>

          {filteredStores.length === 0 ? (
            <EmptyState
              icon={<IoStorefrontOutline className="text-5xl" />}
              title={searchQuery ? 'Nenhuma loja encontrada' : 'Sem lojas nesta categoria'}
              description={
                searchQuery
                  ? `Nao encontramos lojas para "${searchQuery}"`
                  : 'Nao ha lojas disponiveis nesta categoria'
              }
              action={
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedType(null);
                  }}
                  className="text-pagali-purple font-medium hover:underline mt-2"
                >
                  Limpar filtros
                </button>
              }
            />
          ) : (
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'
                  : 'space-y-3'
              }
            >
              {filteredStores.map((store) =>
                viewMode === 'grid' ? (
                  <StoreCard key={store.ID} store={store} onClick={() => handleStoreClick(store.ID)} />
                ) : (
                  <StoreListItem key={store.ID} store={store} onClick={() => handleStoreClick(store.ID)} />
                )
              )}
            </div>
          )}
        </>
      ) : (
        /* Home sections */
        <>
          {/* Featured Stores */}
          {featuredStores.length > 0 && (
            <StoreRow
              title="Lojas em Destaque"
              icon={<FiAward className="text-yellow-500" />}
              stores={featuredStores}
              onStoreClick={handleStoreClick}
            />
          )}

          {/* New Stores */}
          {newStores.length > 0 && (
            <StoreRow
              title="Novidades"
              icon={<FiZap className="text-green-500" />}
              stores={newStores}
              onStoreClick={handleStoreClick}
            />
          )}

          {/* All Stores */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FiTrendingUp className="text-pagali-purple" />
                <h2 className="text-lg font-semibold text-pagali-gray-800">Todas as Lojas</h2>
              </div>
              <div className="flex items-center gap-1 bg-pagali-gray-50 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white text-pagali-purple shadow-sm'
                      : 'text-pagali-gray-400'
                  }`}
                >
                  <FiGrid className="text-sm" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white text-pagali-purple shadow-sm'
                      : 'text-pagali-gray-400'
                  }`}
                >
                  <FiList className="text-sm" />
                </button>
              </div>
            </div>

            {stores.length === 0 ? (
              <EmptyState
                icon={<IoStorefrontOutline className="text-5xl" />}
                title="Sem lojas disponíveis"
                description="De momento não há lojas registadas na plataforma."
              />
            ) : (
              <div
                className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'
                    : 'space-y-3'
                }
              >
                {stores.map((store) =>
                  viewMode === 'grid' ? (
                    <StoreCard key={store.ID} store={store} onClick={() => handleStoreClick(store.ID)} />
                  ) : (
                    <StoreListItem key={store.ID} store={store} onClick={() => handleStoreClick(store.ID)} />
                  )
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Floating Cart */}
      {itemCount > 0 && (
        <button
          onClick={() => navigate('/ecommerce/checkout')}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-pagali-purple text-white rounded-full px-6 py-3.5 shadow-2xl flex items-center gap-3 z-40 hover:bg-pagali-purple-light transition-all hover:scale-105"
        >
          <FiShoppingCart className="text-lg" />
          <span className="font-medium">Carrinho</span>
          <span className="bg-white/20 px-2.5 py-0.5 rounded-full text-sm">
            {itemCount}
          </span>
          <span className="font-semibold">
            {(cartTotal || 0).toLocaleString('pt-CV')} ECV
          </span>
        </button>
      )}
    </div>
  );
};

// List view item
const StoreListItem: React.FC<{ store: Store; onClick: () => void }> = ({ store, onClick }) => {
  const logoSrc = resolveImage(store.LOGO, store.MIME_TYPE);
  const bannerGradient = getTypeColor(store.CATEGORIA_NOME || store.DESCRICAO);
  const initial = (store.NOME || '?').charAt(0).toUpperCase();

  return (
    <div
      onClick={onClick}
      className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 hover:border-pagali-purple/30 hover:shadow-md transition-all cursor-pointer group"
    >
      <div className={`w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br ${bannerGradient} flex items-center justify-center`}>
        {logoSrc ? (
          <img src={logoSrc} alt={store.NOME} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xl font-black text-white/80">{initial}</span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900 truncate group-hover:text-pagali-purple transition-colors text-sm">
            {store.NOME}
          </h3>
          {store.NOVO && (
            <span className="bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded">
              NOVO
            </span>
          )}
          {store.DESTAQUE && (
            <FiStar className="text-yellow-400 flex-shrink-0" />
          )}
        </div>
        {store.DESCRICAO && (
          <p className="text-sm text-pagali-gray-500 truncate mt-0.5">{store.DESCRICAO}</p>
        )}
        <div className="flex items-center gap-3 mt-1 text-xs text-pagali-gray-400">
          {store.CATEGORIA_NOME && (
            <span className="bg-pagali-purple/8 text-pagali-purple px-2 py-0.5 rounded-full font-medium">
              {store.CATEGORIA_NOME}
            </span>
          )}
          <span className="flex items-center gap-1">
            <FiStar className="text-yellow-400" />
            {store.RATING?.toFixed(1) || '4.5'}
          </span>
        </div>
      </div>

      <FiChevronRight className="text-pagali-gray-300 group-hover:text-pagali-purple transition-colors flex-shrink-0" />
    </div>
  );
};

export default Ecommerce;
